import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Film, Eye, EyeOff } from 'lucide-react';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [forgot, setForgot] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('If an account exists for that email, a reset link is on its way. Check your inbox.');
      setForgot(false);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, name);
        toast.success('Account created!');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (err.message === 'Network Error') {
        toast.error('Cannot connect to server. Please try again later.');
      } else {
        toast.error(isLogin ? 'Login failed. Check your credentials or register a new account.' : 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-8 bg-[#060608]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
              <Film className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AvantGarde Media
            </span>
          </div>

          <h1 className="text-4xl font-black tracking-tighter mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {forgot ? 'Reset password' : isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-[#8A8A93] mb-10 text-base">
            {forgot ? "Enter your email and we'll send you a reset link" : isLogin ? 'Sign in to your workspace' : 'Start creating cinematic frames'}
          </p>

          <form onSubmit={forgot ? handleForgot : handleSubmit} className="space-y-5">
            {!isLogin && !forgot && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Name</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="ff-input w-full"
                  placeholder="Your name"
                  required={!isLogin}
                />
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ff-input w-full"
                placeholder="you@example.com"
                required
              />
            </div>
            {!forgot && (
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Password</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ff-input w-full pr-12"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setForgot(true)}
                  className="mt-2 text-xs text-[#FF4500] hover:text-[#FF5722] transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="ff-btn-primary w-full text-center flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                forgot ? 'Send reset link' : isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {forgot ? (
            <p className="mt-8 text-center text-[#8A8A93] text-sm">
              <button
                data-testid="forgot-back-link"
                onClick={() => setForgot(false)}
                className="text-[#FF4500] hover:text-[#FF5722] font-medium transition-colors"
              >
                Back to sign in
              </button>
            </p>
          ) : (
          <p className="mt-8 text-center text-[#8A8A93] text-sm">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              data-testid="auth-toggle-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#FF4500] hover:text-[#FF5722] font-medium transition-colors"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
          )}
        </div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:block w-[45%] ff-auth-side relative">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#060608]/80" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-white/90 text-xl font-semibold leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Generate first & last frames for AI video production.
            <span className="text-[#FF4500]"> Paste. Generate. Create.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
